import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { DialogActionsContainer } from '@components/ConfirmPopup'
import CustomPopover, { CustomPopoverProps } from '@components/CustomPopover'
import MultiSelect from '@modules/search/components/FiltersSection/MultiSelect'
import { Shortlist } from '@modules/shortlist/types'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import useSelection from '@shared/hooks/useSelection'
import { colors } from '@theme/variables'

import { ShortlistItemsContainer } from '../ShortlistProfileButton/index.styles'

interface MoveProfilesPopupProps {
  onConfirmClick: (selectedIds: Shortlist['id'][], copyProspects?: boolean) => void
  shortlists: Shortlist[]
  canCopy?: boolean
  onCancelClick?: () => void
  shouldClose?: (v: unknown) => void
}

const MoveProfilesPopup = ({
  anchor,
  shortlists,
  setAnchor,
  onCancelClick,
  onConfirmClick,
  shouldClose,
  canCopy,
}: MoveProfilesPopupProps & Pick<CustomPopoverProps, 'anchor' | 'setAnchor'>) => {
  const { t } = useTranslation()
  const { isDesktop } = useScreenDimensions()

  const shortlistIds = useMemo(() => shortlists.map(i => i.id), [shortlists])
  const shortlistOptions = useMemo(() => shortlists.map(i => ({ label: i.name, value: i.id })), [shortlists])
  const [copyProspects, setCopyProspects] = useState<boolean | undefined>(undefined)
  const headerLabelKey = useMemo(
    () => (canCopy ? translationKeys.MOVE_PROSPECTS : translationKeys.MOVE_PROSPECT_TO_OTHER_CUSTOM_LISTS),
    [canCopy],
  )
  const {
    toggleSelectItem: toggleShortlistItem,
    selectedIds: selectedShortlistIds,
    changedItems,
  } = useSelection(shortlistIds, { trackChanges: true })

  const handleConfirm = () => {
    onConfirmClick(selectedShortlistIds, copyProspects)
  }

  const handleCancel = () => {
    onCancelClick?.()
    setAnchor(null)
  }

  const handleCancelWithConfirmation = () => {
    const proceed = shouldClose?.(changedItems) ?? true
    if (proceed) handleCancel()
  }

  if (!isDesktop) {
    return (
      <Dialog open={Boolean(anchor)} onClose={handleCancelWithConfirmation} fullWidth maxWidth="xs">
        <DialogContent>
          <Box width="100%">
            <Box maxWidth="230px" mb="10px">
              <Typography variant="h2" alignContent="center">
                {t(headerLabelKey)}
                <DriveFileMoveOutlinedIcon sx={{ ml: '6px', mb: '-4px' }} fontSize="small" />
              </Typography>
            </Box>

            <ShortlistItemsContainer $maxHeight="300px">
              <MultiSelect values={selectedShortlistIds} label="" options={shortlistOptions} onChange={toggleShortlistItem} />
            </ShortlistItemsContainer>

            {canCopy && (
              <Box mt="20px" mb="10px">
                <Divider sx={{ marginBottom: '20px', borderColor: colors.black }} />
                <Box display="flex" alignItems="center">
                  <Checkbox
                    sx={{
                      marginLeft: '-10px',
                      marginTop: '-10px',
                      marginBottom: '-10px',
                    }}
                    checked={!!copyProspects}
                    onChange={() => setCopyProspects(!copyProspects)}
                  />
                  <Typography>{t(translationKeys.KEEP_PROSPECT_COPY_IN_ORIGNAL_LIST)}</Typography>
                </Box>
              </Box>
            )}
          </Box>

          <DialogActionsContainer>
            <Button variant="outlined" onClick={handleCancel} data-testid="cancel_move_profiles_to_shortlists_btn">
              {t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              data-testid="confirm_move_profiles_to_shortlists_btn"
              disabled={!selectedShortlistIds.length}
            >
              {t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
            </Button>
          </DialogActionsContainer>
        </DialogContent>
      </Dialog>
    )
  }

  // --- DESKTOP: original Popover ---
  return (
    <CustomPopover id="move_profiles_to_shortlists" anchor={anchor} setAnchor={handleCancelWithConfirmation}>
      <Box padding="20px" width={265}>
        <Box maxWidth="230px" mb="10px">
          <Typography variant="h2" alignContent="center">
            {t(headerLabelKey)}
            <DriveFileMoveOutlinedIcon sx={{ ml: '6px', mb: '-4px' }} fontSize="small" />
          </Typography>
        </Box>
        <ShortlistItemsContainer $maxHeight="300px">
          <MultiSelect values={selectedShortlistIds} label="" options={shortlistOptions} onChange={toggleShortlistItem} />
        </ShortlistItemsContainer>
        {canCopy && (
          <Box mt="20px" mb="10px">
            <Divider sx={{ marginBottom: '20px', borderColor: colors.black }} />
            <Box display="flex" alignItems="center">
              <Checkbox
                sx={{ marginLeft: '-10px', marginTop: '-10px', marginBottom: '-10px' }}
                checked={!!copyProspects}
                onChange={() => setCopyProspects(!copyProspects)}
              />
              <Typography>{t(translationKeys.KEEP_PROSPECT_COPY_IN_ORIGNAL_LIST)}</Typography>
            </Box>
          </Box>
        )}
      </Box>

      <DialogActionsContainer>
        <Button variant="outlined" onClick={handleCancel} data-testid="cancel_move_profiles_to_shortlists_btn">
          {t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          data-testid="confirm_move_profiles_to_shortlists_btn"
          disabled={!selectedShortlistIds?.length}
        >
          {t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
        </Button>
      </DialogActionsContainer>
    </CustomPopover>
  )
}

export default memo(MoveProfilesPopup)
